import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { Loader2, Save, Timer } from 'lucide-react'
import { toast } from 'sonner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useLoopIntervals, useUpdateLoopIntervals } from '@/lib/api'

const MAX_INTERVAL = 60.0

export function LoopIntervalSettings() {
  const { t } = useTranslation()
  const { data, isLoading } = useLoopIntervals()
  const updateMutation = useUpdateLoopIntervals()

  const [searchInterval, setSearchInterval] = useState('')
  const [optimizationInterval, setOptimizationInterval] = useState('')

  useEffect(() => {
    if (data) {
      setSearchInterval(String(data.search_loop_interval))
      setOptimizationInterval(String(data.optimization_loop_interval))
    }
  }, [data])

  const isValidInterval = (value: string) => {
    const num = parseFloat(value)
    return !isNaN(num) && num > 0 && num <= MAX_INTERVAL
  }

  const hasChanges =
    data &&
    (String(data.search_loop_interval) !== searchInterval ||
      String(data.optimization_loop_interval) !== optimizationInterval)

  const canSave =
    hasChanges &&
    isValidInterval(searchInterval) &&
    isValidInterval(optimizationInterval)

  const handleSave = () => {
    updateMutation.mutate(
      {
        search_loop_interval: parseFloat(searchInterval),
        optimization_loop_interval: parseFloat(optimizationInterval),
      },
      {
        onSuccess: () => {
          toast.success(t('advanced.loopIntervals.saved'))
        },
        onError: () => {
          toast.error(t('advanced.loopIntervals.error'))
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Timer className="h-5 w-5" />
          {t('advanced.loopIntervals.title')}
        </CardTitle>
        <CardDescription>
          {t('advanced.loopIntervals.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('advanced.loopIntervals.loading')}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="searchLoopInterval">
                {t('advanced.loopIntervals.searchInterval')}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="searchLoopInterval"
                  type="number"
                  inputMode="decimal"
                  min={0.001}
                  max={MAX_INTERVAL}
                  step={0.01}
                  value={searchInterval}
                  onChange={(e) => setSearchInterval(e.target.value)}
                  className="w-32"
                />
                <span className="text-sm text-muted-foreground">
                  {t('advanced.loopIntervals.seconds')}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('advanced.loopIntervals.searchIntervalDescription')}
              </p>
              {searchInterval && !isValidInterval(searchInterval) && (
                <p className="text-xs text-destructive">
                  {t('advanced.loopIntervals.validation')}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="optimizationLoopInterval">
                {t('advanced.loopIntervals.optimizationInterval')}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="optimizationLoopInterval"
                  type="number"
                  inputMode="decimal"
                  min={0.001}
                  max={MAX_INTERVAL}
                  step={0.01}
                  value={optimizationInterval}
                  onChange={(e) => setOptimizationInterval(e.target.value)}
                  className="w-32"
                />
                <span className="text-sm text-muted-foreground">
                  {t('advanced.loopIntervals.seconds')}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('advanced.loopIntervals.optimizationIntervalDescription')}
              </p>
              {optimizationInterval &&
                !isValidInterval(optimizationInterval) && (
                  <p className="text-xs text-destructive">
                    {t('advanced.loopIntervals.validation')}
                  </p>
                )}
            </div>

            <Button
              onClick={handleSave}
              disabled={!canSave || updateMutation.isPending}
              className="w-full"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {t('advanced.loopIntervals.save')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 me-2" />
                  {t('advanced.loopIntervals.save')}
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
